import upperFirst from 'lodash/upperFirst';
import { CountryCode } from '@/store/ui'
import libphonenumber, { PhoneNumberFormat } from 'google-libphonenumber';
import moment from 'moment';

export function joinWithSuffix(items: string[], separator: string = ', ', suffix: string = ' & '): string {
  if (items.length == 0) {
    return '';
  }

  if (items.length == 1) {
    return items[0];
  }

  if (items.length == 2) {
    return `${items[0]}${suffix}${items[1]}`;
  }

  return `${items.slice(0, items.length - 1).join(`${separator}`)}${suffix}${items[items.length - 1]}`;
}

export function slugify(str: string): string {
  return str.replace(/[ ]/g, '-').toLowerCase();
}

export function camelCaseObject<T extends object>(obj: object): T {
  return caseObject<T>(obj, str => str[0].toLocaleLowerCase().concat(str.slice(1)));
}

export function pascalCaseObject<T extends object>(obj: object): T {
  return caseObject<T>(obj, str => str[0].toLocaleUpperCase().concat(str.slice(1)));
}

export function numberToWords(num: number): string {
  switch (num) {
    case 0:
      return 'zero';
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    case 9:
      return 'nine';
    case 10:
      return 'ten';
    default:
      return 'more than ten';
  }
}

/** Formats phone number from 5551234567 -> (555) 123-4567 */
export function formatPhoneNumber(phoneNumber: string, countryCode: string = CountryCode.US): string {
  const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
  const number = phoneUtil.parse(phoneNumber, countryCode);

  switch (countryCode) {
    case CountryCode.GB:
    case CountryCode.UK:
      return phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL)
    case CountryCode.CA:
    case CountryCode.US:
    default:
      return phoneUtil.format(number, PhoneNumberFormat.NATIONAL)
  }
}

export function reverseFormatUKPhoneNumber (phoneNumber: string) {
  return (phoneNumber.slice(0, 2) === '44' && [12, 13].includes(phoneNumber.length))
    ? phoneNumber.substring(2)
    : phoneNumber;
}

//TODO Implement a function can interprets the mask value coming from the country in the constans.ts file to work more generically. 
export function formatPostalCode(code: string, countryCode: string = CountryCode.US): string {
  switch (countryCode) {
    case CountryCode.CA:
      return formatCanadaPostalCode(code);
    case CountryCode.GB:
    case CountryCode.US:      
    default:
      return code;
  }
}

function formatCanadaPostalCode(code: string): string {
  let result = '';

  code = code.toUpperCase();

  // when autofilling from search
  if (code.length == 7) {
    result = code;
  } else if (code.length < 3) {
    result = code;
  } else if (code.length == 3) {
    result = code + ' ';
  } else if (code.length > 3) {
    result = `${code.substring(0, 3)} ${code.substring(3)}`;
  }

  return result;
}

/** Formats expiration date from 1123 -> 11/23/45 */
export function formatDate(date: string): string {
  return `${date.substring(0, 2)}/${date.substring(2, 4)}`
}

export function formatBirthdate(date: string, countryCode: string, separator: string, birthdateEnabled: boolean = false): string {
  let month = '';
  let day = '';
  let year = '1972';
  let enabledFormatter = '';
  let disableFormatter = '';

  if (date) {
    switch (countryCode) {
      case CountryCode.GB:
        month = date.slice(2);
        day = date.slice(0, 2);

        enabledFormatter = `${day}${separator}${month}`;
        disableFormatter = `${day}${separator}${month}`;
        break;
      case CountryCode.CA:
      case CountryCode.US:
      default:
        month = date.slice(0, 2);
        day = date.slice(2, 4);
        year = date.slice(4);

        enabledFormatter = `${month}${separator}${day}${separator}${year}`;
        disableFormatter = `${month}${separator}${day}`;
        break;
    }

    return birthdateEnabled ? enabledFormatter : disableFormatter;
  }

  return '';
}

export function formatExpirationDate(orderDateTime: string, countryCode: string): string {
  return formatOrderDate(orderDateTime, countryCode);
}

export function getFullMonthName(date: Date) {
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return months[date.getMonth()];
}

export function formatOfferEndsDate(date: Date) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
  const dayName = days[date.getUTCDay()];
  const month = months[date.getUTCMonth()];
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${dayName}, ${month}. ${day}`;
}

export function formatOrderDate(orderDateTime: string, countryCode: string): string {
  switch (countryCode) {
    case CountryCode.CA:
    case CountryCode.US:   
      return moment(orderDateTime).format('MM/DD/YYYY');
    case CountryCode.GB:   
      return moment(orderDateTime).format('DD/MM/YYYY');
    default:
      return orderDateTime;
  }
}

export function formatGiftCardNumberWithSpaces(giftCardNumber: string): string {
  return [...giftCardNumber]
    .map((digit, i) => (i % 4 == 0 ? ` ${digit}` : digit))
    .join('')
    .trim();
}

function caseString(str: string | null, transformFn: (str: string) => string): string {
  if (!str) {
    return '';
  }

  return transformFn(str);
}

function caseObject<T extends object>(obj: object, transformFn: (str: string) => string): T {
  return Object.entries(obj).reduce((acc, entry) => {
    const key = caseString(entry[0], transformFn);
    const value = !!entry[1] && !Array.isArray(entry[1]) && typeof entry[1] === 'object' ? caseObject(entry[1], transformFn) : entry[1];

    acc[key] = value;

    return acc;
  }, {} as any);
}

// data & qa title => Data & Qa Title
export function capitalizeEveryWord(str?: string): string {
  if (!str) {
    return '';
  }

  return str
    .split(' ')
    .map(upperFirst)
    .join(' ');
}

// DATA & QA TITLE => Data & Qa Title
export function toTitleCase(str?: string): string {
  if (!str) {
    return '';
  }

  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function addLeadingZero(num: number): string {
  if(num >= 0) {
    return num.toString().padStart(2, '0')
  }
  return '';
}

export function removeSpecialCharacters(str?: string): string | undefined {
  return str?.replace(/[^a-zA-Z0-9 -]/g, '');
}